import React from "react";
import { Link } from "react-router-dom";
import { useSEO } from "../lib/seo";

const CREATES = [
  ["Custom T-shirts", "Personalised tees made around your design, text or idea."],
  ["Oversized tees", "Relaxed-fit apparel for custom graphics and everyday wear."],
  ["Hoodies & sports jerseys", "Personalised options for teams, groups and individual orders."],
  ["Corporate & college apparel", "Custom apparel for teams, organisations, events and groups."],
];

const WHO_WE_SERVE = [
  "Individuals and personal orders",
  "Couples and families",
  "Sports teams and groups",
  "Schools and colleges",
  "Businesses and offices",
  "Events and organisations",
];

export default function About() {
  useSEO({
    title: "About — PrintMyTees",
    description: "Learn about PrintMyTees, a custom apparel brand from Tamil Nadu, India, creating personalised T-shirts, hoodies and sportswear.",
  });

  return (
    <div className="container-pmt py-10 sm:py-12" data-testid="about-page">
      <div className="overline">About</div>
      <div className="mt-2 grid grid-cols-1 lg:grid-cols-[1.05fr_.95fr] gap-8 lg:gap-12 items-center">
        <div>
          <h1 className="display-h2">PrintMyTees.</h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg leading-7 text-stone-600">
            A custom apparel brand from Tamil Nadu, India — focused on personalised T-shirts, hoodies and sportswear.
          </p>
          <p className="mt-4 max-w-2xl text-sm sm:text-base leading-7 text-stone-700">
            We help individuals, couples, families, schools, colleges, sports teams, businesses, offices, events and organisations bring their ideas to fabric.
          </p>
          <div className="mt-6 border-l-2 border-[#D95328] pl-4">
            <div className="overline">Our tagline</div>
            <div className="mt-1 text-2xl sm:text-3xl font-bold uppercase" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              Wear Your Story.
            </div>
          </div>
        </div>

        <div className="aspect-[4/5] bg-[#F3EDE2] border border-[#E6DFD5] overflow-hidden">
          <img
            src="https://images.pexels.com/photos/27893067/pexels-photo-27893067.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Custom apparel printing workspace"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      <section className="mt-14 sm:mt-16">
        <div className="overline">What We Create</div>
        <h2 className="display-h2 mt-2 max-w-3xl">Custom apparel for everyday ideas and group needs.</h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {CREATES.map(([title, text]) => (
            <div key={title} className="border border-[#E6DFD5] bg-white p-5">
              <h3 className="font-bold uppercase text-xl" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{title}</h3>
              <p className="mt-2 text-sm leading-6 text-stone-600">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14 sm:mt-16">
        <div className="overline">Who We Serve</div>
        <h2 className="display-h2 mt-2 max-w-3xl">From one personalised tee to a larger group order.</h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {WHO_WE_SERVE.map((item) => (
            <div key={item} className="border border-[#E6DFD5] bg-[#F3EDE2] p-4 text-sm text-stone-700">
              {item}
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14 sm:mt-16 border border-[#E6DFD5] bg-[#F3EDE2] p-5 sm:p-6">
        <div className="overline">Make It Yours</div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-bold uppercase" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
          Choose a design or bring your own idea.
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">
          Browse ready-to-buy products and designs, or tell us what you want for a custom order.
        </p>
        <div className="mt-5 flex flex-col sm:flex-row flex-wrap gap-3">
          <Link to="/shop" className="btn-primary w-full sm:w-auto text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2">
            Shop Products
          </Link>
          <Link to="/designs" className="btn-outline-pmt w-full sm:w-auto text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2">
            Browse Designs
          </Link>
          <Link to="/custom-orders" className="btn-outline-pmt w-full sm:w-auto text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2">
            Start a Custom Order
          </Link>
        </div>
      </section>
    </div>
  );
}
